import { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import Grid from '../components/grid';
import ProductGridItems from '../components/layout/product-grid-items';
import Collections from '../components/layout/search/collections';
import FilterList from '../components/layout/search/filter';
import { defaultSort, sorting } from '../lib/constants';
import { getProducts, getCollectionProducts } from '../lib/burokshop';
import type { Product } from '../lib/burokshop/types';
import { useMarketplace } from '../context/MarketplaceContext';

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { marketplaceType } = useMarketplace();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const collection = params.collection || '';

  useEffect(() => {
    async function loadProducts() {
      setLoading(true);
      const sort = searchParams.get('sort') || '';
      const searchValue = searchParams.get('q') || '';
      const { sortKey, reverse } = sorting.find((item) => item.slug === sort) || defaultSort;

      let productsData: Product[];

      if (collection) {
        // Si on est sur une collection spécifique
        productsData = await getCollectionProducts({
          collection,
          sortKey,
          reverse,
          marketplaceType
        });
      } else {
        // Sinon, récupérer tous les produits avec le tri
        productsData = await getProducts({ 
          sortKey, 
          reverse, 
          query: searchValue,
          marketplaceType 
        });
      }

      setProducts(productsData);
      setLoading(false);
    }
    loadProducts();
  }, [searchParams, marketplaceType, collection]);

  const searchValue = searchParams.get('q') || '';
  const resultsText = products.length > 1 ? 'résultats' : 'résultat';

  if (loading) {
    return (
      <div className="mx-auto flex max-w-screen-2xl flex-col gap-8 px-4 pt-8 pb-4 text-black md:flex-row dark:text-white">
        <div className="order-first w-full flex-none md:max-w-[125px]">
          <div className="mb-3 h-4 w-5/6 animate-pulse rounded-sm bg-neutral-400 dark:bg-neutral-700" />
        </div>
        <div className="order-last min-h-screen w-full md:order-none">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-lg">Un instant...</div>
          </div>
        </div>
        <div className="order-none flex-none md:order-last md:w-[125px]">
          <div className="mb-3 h-4 w-5/6 animate-pulse rounded-sm bg-neutral-400 dark:bg-neutral-700" />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-screen-2xl flex-col gap-8 px-4 pt-8 pb-4 text-black md:flex-row dark:text-white">
      {/* Colonne gauche : Collections */}
      <div className="order-first w-full flex-none md:max-w-[125px]">
        <Collections />
      </div>

      {/* Colonne centrale : Produits */}
      <div className="order-last min-h-screen w-full md:order-none">
        {searchValue ? (
          <p className="mb-4">
            {products.length === 0
              ? 'Aucun produit ne correspond à '
              : `Affichage de ${products.length} ${resultsText} pour `}
            <span className="font-bold">&quot;{searchValue}&quot;</span>
          </p>
        ) : null}
        {products.length > 0 ? (
          <Grid className="grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <ProductGridItems products={products} />
          </Grid>
        ) : (
          <div className="py-12 text-center">
            <p className="text-lg text-neutral-500 dark:text-neutral-400">
              Aucun produit trouvé
            </p>
          </div>
        )}
      </div>

      {/* Colonne droite : Filtres de tri */}
      <div className="order-none flex-none md:order-last md:w-[125px]">
        <FilterList list={sorting} title="Sort by" />
      </div>
    </div>
  );
}

